/**
 * Drawing and Trendline Line Style Utilities.
 *
 * Implements line dash pattern resolution (solid: 0, dotted: 1, dashed: 2)
 * and trendline settings persistence and deserialization.
 */

export type LineStyle = 0 | 1 | 2;

export interface TrendlineElementStyle {
  color: string;
  width: number;
}

export interface TrendlineStyle {
  support: TrendlineElementStyle;
  resistance: TrendlineElementStyle;
  lineStyle: number;
}

export const DEFAULT_TRENDLINE_STYLE: TrendlineStyle = {
  support: { color: "#2DBE8E", width: 2 },
  resistance: { color: "#F06A6A", width: 2 },
  lineStyle: 0,
};

/**
 * Returns the line dash array for canvas and overlay contexts.
 */
export function getLineDashPattern(
  lineStyle: number,
  variant: "canvas" | "overlay" = "canvas",
): number[] {
  if (variant === "overlay") {
    return lineStyle === 1 ? [2, 4] : lineStyle === 2 ? [8, 6] : [];
  }
  return lineStyle === 1 ? [4, 4] : lineStyle === 2 ? [2, 2] : [];
}

/**
 * Parses raw JSON string from localStorage into TrendlineStyle with defaults fallback.
 */
export function parseTrendlineStyle(
  raw: string | null | undefined,
): TrendlineStyle {
  if (!raw) return { ...DEFAULT_TRENDLINE_STYLE };
  try {
    const parsed = JSON.parse(raw);
    return {
      support: {
        ...DEFAULT_TRENDLINE_STYLE.support,
        ...(parsed.support || {}),
      },
      resistance: {
        ...DEFAULT_TRENDLINE_STYLE.resistance,
        ...(parsed.resistance || {}),
      },
      lineStyle:
        parsed.lineStyle !== undefined
          ? parsed.lineStyle
          : DEFAULT_TRENDLINE_STYLE.lineStyle,
    };
  } catch {
    return { ...DEFAULT_TRENDLINE_STYLE };
  }
}

/**
 * Serializes TrendlineStyle into JSON string for localStorage.
 */
export function serializeTrendlineStyle(style: TrendlineStyle): string {
  return JSON.stringify(style);
}
